import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { formatErrorDetail } from "../lib/api";

export default function AdminLogin() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/admin" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/admin");
        } catch (err) {
            toast.error(formatErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left brand panel */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] text-white relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-30 bg-cover bg-center"
                    style={{ backgroundImage: "url(https://images.pexels.com/photos/36327497/pexels-photo-36327497.jpeg)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/80 to-[#0F172A]" />
                <div className="relative z-10 p-16 flex flex-col justify-between w-full">
                    <div className="flex items-center gap-3">
                        <div className="h-14 w-14 bg-black flex items-center justify-center overflow-hidden">
                            <img src="https://customer-assets.emergentagent.com/job_form-manage/artifacts/dwtsdzv7_WhatsApp%20Image%202026-05-25%20at%208.11.14%20PM.jpeg" alt="STMF" className="h-full w-full object-cover" />
                        </div>
                        <div>
                            <div className="font-display font-bold text-lg">STMF</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Admin Portal · Est. 1987</div>
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Federation Operations</div>
                        <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight mb-4">Manage members, applications &amp; events.</h1>
                        <p className="text-white/70 max-w-md">Authorized personnel only. All sessions are logged and monitored.</p>
                    </div>
                    <div className="text-xs text-white/40">© 2026 Surat Texmac Federation</div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-white">
                <div className="w-full max-w-md">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Admin Sign In</div>
                    <h2 className="font-display text-3xl font-bold text-[#0F172A] mb-2">Welcome back.</h2>
                    <p className="text-[#64748B] text-sm mb-10">Enter your credentials to access the federation dashboard.</p>

                    <form onSubmit={onSubmit} data-testid="admin-login-form" className="space-y-5">
                        <div>
                            <label className="block text-xs uppercase tracking-[0.1em] font-medium text-[#64748B] mb-2">Email</label>
                            <input
                                type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="w-full px-4 py-3 border border-[#E2E8F0] text-sm text-[#0F172A] focus:outline-none focus:border-[#0F172A]"
                                placeholder="admin@stmf.org"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-[0.1em] font-medium text-[#64748B] mb-2">Password</label>
                            <input
                                type="password" required value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                className="w-full px-4 py-3 border border-[#E2E8F0] text-sm text-[#0F172A] focus:outline-none focus:border-[#0F172A]"
                                placeholder="••••••••"
                            />
                        </div>
                        <button
                            type="submit" disabled={loading}
                            data-testid="admin-login-submit"
                            className="w-full px-6 py-4 bg-[#0F172A] text-white text-sm font-semibold uppercase tracking-[0.08em] hover:bg-[#1E293B] transition-colors disabled:opacity-60"
                        >{loading ? "Signing in…" : "Sign In"}</button>
                    </form>

                    <div className="mt-12 pt-6 border-t border-[#E2E8F0] text-xs text-[#64748B]">
                        <div className="font-mono mb-2 text-[#0F172A]">Default credentials for review:</div>
                        <div className="font-mono">admin@stmf.org · StmfAdmin@2026</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
